import React, { useEffect, useMemo, useState } from 'react';
import { Search, ShieldCheck, ShieldOff, UserCheck, UserX, RefreshCw, Store, Trash2, Pencil, Loader2, CheckSquare, Square, X } from 'lucide-react';
import { adminApi } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [vendorDialog, setVendorDialog] = useState({ open: false, user: null, vendor_id: '' });
  const [editDialog, setEditDialog] = useState({ open: false, user: null, name: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { user: me } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const [u, vs] = await Promise.all([
        adminApi.users(),
        adminApi.vendors().catch(() => []),
      ]);
      setUsers(u); setVendors(vs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Live client-side filtering — snappier than a submit-driven server round-trip
  // for the ~200-user Ottawa/Ibadan pilot. Backend search still works if we ever
  // exceed the list size, but this covers the common case.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const hay = [u.name, u.email, u.phone, u.user_id, u.provider, u.vendor_id]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  const toggleAdmin = async (u) => {
    try {
      const updated = await adminApi.updateUser(u.user_id, { is_admin: !u.is_admin });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, is_admin: updated.is_admin } : x)));
      toast.success(updated.is_admin ? 'Promoted to admin' : 'Admin revoked');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (u) => {
    try {
      const updated = await adminApi.updateUser(u.user_id, { is_active: u.is_active === false ? true : false });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, is_active: updated.is_active } : x)));
      toast.success(updated.is_active === false ? 'Account disabled' : 'Account enabled');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openVendorDialog = (u) => setVendorDialog({ open: true, user: u, vendor_id: u.vendor_id || '' });

  const saveVendor = async () => {
    const u = vendorDialog.user;
    try {
      const updated = await adminApi.updateUser(u.user_id, { vendor_id: vendorDialog.vendor_id || '' });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, vendor_id: updated.vendor_id } : x)));
      toast.success(updated.vendor_id ? 'Vendor assigned' : 'Vendor cleared');
      setVendorDialog({ open: false, user: null, vendor_id: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (u) => setEditDialog({ open: true, user: u, name: u.name || '' });

  const saveEdit = async () => {
    const u = editDialog.user;
    if (!u) return;
    setSavingEdit(true);
    try {
      const updated = await adminApi.updateUser(u.user_id, { name: editDialog.name });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, name: updated.name } : x)));
      toast.success('User saved');
      setEditDialog({ open: false, user: null, name: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    finally { setSavingEdit(false); }
  };

  const removeUser = async (u) => {
    if (u.user_id === me?.id) { toast.error("You can't delete your own account."); return; }
    if (!window.confirm(`Permanently delete "${u.name || u.email}"?\n\nThis unlinks any vendor/store they own and removes their saved addresses. Historical orders stay in the system for accounting.`)) return;
    try {
      await adminApi.deleteUser(u.user_id);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success('User deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  // -------- Bulk selection helpers --------
  const toggleSelect = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const allSelectableIds = useMemo(
    () => filtered.filter((u) => u.user_id !== me?.id).map((u) => u.user_id),
    [filtered, me?.id],
  );
  const allSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        allSelectableIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...allSelectableIds]);
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, verb) => {
    const ids = [...selected].filter((id) => id !== me?.id);
    if (ids.length === 0) { toast.error('Nothing selected'); return; }
    const isDestructive = action === 'delete';
    if (isDestructive && !window.confirm(`Permanently delete ${ids.length} user(s)? Historical orders will stay for accounting.`)) return;
    setBulkBusy(true);
    try {
      const res = await adminApi.bulkUsers(ids, action);
      const ok = res?.counts?.succeeded ?? 0;
      const fail = res?.counts?.failed ?? 0;
      toast.success(`${verb} ${ok} user${ok === 1 ? '' : 's'}${fail ? ` · ${fail} failed` : ''}`);
      if (fail && res?.errors) {
        const firstErr = Object.entries(res.errors)[0];
        if (firstErr) toast.error(`${firstErr[0].slice(0, 12)}…: ${firstErr[1]}`);
      }
      clearSelection();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1">
            Manage {filtered.length}{q.trim() ? ` of ${users.length}` : ''} customer accounts — edit names, assign vendor stores, revoke admin, or delete.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone…"
              className="pl-9 h-10 w-72"
              data-testid="users-search-input"
            />
          </div>
          <Button variant="outline" onClick={load}><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {selected.size > 0 && (
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-900 text-white flex items-center gap-2 flex-wrap" data-testid="users-bulk-toolbar">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <button type="button" onClick={clearSelection} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="users-bulk-clear">
              <X className="h-3.5 w-3.5" />Clear
            </button>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('disable', 'Disabled')} data-testid="users-bulk-disable">
                <UserX className="h-3.5 w-3.5 mr-1" />Disable
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('enable', 'Enabled')} data-testid="users-bulk-enable">
                <UserCheck className="h-3.5 w-3.5 mr-1" />Enable
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('promote', 'Promoted')} data-testid="users-bulk-promote">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />Promote to admin
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('revoke_admin', 'Revoked admin from')} data-testid="users-bulk-revoke">
                <ShieldOff className="h-3.5 w-3.5 mr-1" />Revoke admin
              </Button>
              <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('delete', 'Deleted')} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="users-bulk-delete">
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}Delete
              </Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-10 px-3 py-3">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={allSelectableIds.length === 0}
                    className="text-slate-500 hover:text-slate-900 disabled:opacity-40"
                    data-testid="users-select-all"
                    title={allSelected ? 'Clear selection' : 'Select all visible users (except you)'}
                  >
                    {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="text-left font-medium px-4 py-3">User</th>
                <th className="text-left font-medium px-4 py-3">Provider</th>
                <th className="text-left font-medium px-4 py-3">Orders</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center text-slate-500 py-10" data-testid="users-empty-state">{users.length === 0 ? 'No users found.' : 'No matches for this search.'}</td></tr>
              ) : filtered.map((u) => {
                const isSelf = u.user_id === me?.id;
                const isSel = selected.has(u.user_id);
                return (
                <tr key={u.user_id} className={`hover:bg-slate-50 ${isSel ? 'bg-slate-50' : ''}`} data-testid={`user-row-${u.user_id}`}>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      disabled={isSelf}
                      onClick={() => toggleSelect(u.user_id)}
                      className="text-slate-500 hover:text-slate-900 disabled:opacity-30"
                      data-testid={`users-select-${u.user_id}`}
                      title={isSelf ? "You can't bulk-modify your own account" : 'Select'}
                    >
                      {isSel ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full" /> : (
                        <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{(u.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{u.name}{u.user_id === me?.id && <span className="ml-2 text-xs text-emerald-700">(you)</span>}</div>
                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{u.provider || 'email'}</td>
                  <td className="px-4 py-3 text-slate-600">{u.orders_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {u.is_admin && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0">Admin</Badge>}
                      {u.vendor_id && <Badge className="bg-blue-50 text-blue-700 border-0 hover:bg-blue-50"><Store className="h-3 w-3 mr-1" />{vendorName(u.vendor_id)}</Badge>}
                      {u.is_active === false ? (
                        <Badge className="bg-rose-50 text-rose-700 border-0 hover:bg-rose-50">Disabled</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-100">Active</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="mr-2" data-testid={`user-edit-${u.user_id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openVendorDialog(u)} className="mr-2" data-testid={`user-vendor-${u.user_id}`}><Store className="h-3.5 w-3.5 mr-1" />{u.vendor_id ? 'Change store' : 'Assign store'}</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleAdmin(u)} className="mr-2">
                      {u.is_admin ? <><ShieldOff className="h-3.5 w-3.5 mr-1" />Revoke admin</> : <><ShieldCheck className="h-3.5 w-3.5 mr-1" />Make admin</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(u)} className={u.is_active === false ? 'mr-2' : 'text-rose-600 hover:text-rose-700 border-rose-200 mr-2'}>
                      {u.is_active === false ? <><UserCheck className="h-3.5 w-3.5 mr-1" />Enable</> : <><UserX className="h-3.5 w-3.5 mr-1" />Disable</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeUser(u)}
                      className="text-rose-600 hover:text-rose-700 border-rose-200"
                      data-testid={`user-delete-${u.user_id}`}
                      disabled={u.user_id === me?.id}
                      title={u.user_id === me?.id ? "You can't delete your own account" : 'Permanently delete user'}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={vendorDialog.open} onOpenChange={(o) => setVendorDialog((d) => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign vendor store</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Link {vendorDialog.user?.name} to a vendor. They will get vendor-admin access at /vendor-admin.</p>
          <div className="mt-3">
            <Select value={vendorDialog.vendor_id || '__none__'} onValueChange={(v) => setVendorDialog((d) => ({ ...d, vendor_id: v === '__none__' ? '' : v }))}>
              <SelectTrigger className="h-10"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No vendor (clear) —</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVendorDialog({ open: false, user: null, vendor_id: '' })}>Cancel</Button>
            <Button onClick={saveVendor} className="bg-emerald-600 hover:bg-emerald-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, user: null, name: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update this account&apos;s display name. Email is fixed by the auth provider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</label>
              <Input
                value={editDialog.name}
                onChange={(e) => setEditDialog((d) => ({ ...d, name: e.target.value }))}
                data-testid="edit-user-name"
                autoFocus
              />
            </div>
            <div className="text-xs text-slate-500">
              Email: <span className="font-mono text-slate-700">{editDialog.user?.email}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null, name: '' })}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !editDialog.name?.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-user-save">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
